import RefreshIcon from "@mui/icons-material/CachedRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import { Alert, Button, Divider, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import MarkdownPreview from "@entrilia/shared/components/markdown/MarkdownPreview";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../../api/adminApi";
import type { ObjectFieldValues } from "../../../../../api/types/objectTypes";
import type {
  ObjectSelectConfiguration,
  Workflow,
  WorkflowActivity,
  WorkflowActivityChangeSet,
} from "../../../../../api/types/workflowTypes";
import WorkflowActionConfirmationDialog from "../../../../approvals/workflows/WorkflowActionConfirmationDialog";
import WorkflowSignalActionButton from "../../../../approvals/workflows/WorkflowSignalActionButton";
import GeneralPageFooter from "../../../../common/GeneralPageFooter";
import ActivityEditDialog from "./edit-activity/ActivityEditDialog";
import {
  getInitialInlineChangeSetEditState,
  getMissingRequiredFieldCaptions,
  mapStateToUpdateWorkflowActivityChangeSetsRequest,
  updateChangeSetFieldAction,
  updateChangeSetFieldsAction,
} from "./edit-activity/changeSetEditState";
import ChangeSetInlineEditSection from "./edit-activity/ChangeSetInlineEditSection";
import { buildObjectFieldUpdates } from "./edit-activity/objectSelectOptionsHelper";
import WorkflowActivityChangeSetSection from "./WorkflowActivityChangeSetSection";
import WorkflowActivityCitations from "./WorkflowActivityCitations";
import WorkflowActivityDetailInfoSection from "./WorkflowActivityDetailInfoSection";
import WorkflowActivityExecutionLogsSection from "./WorkflowActivityExecutionLogsSection";
import WorkflowActivityRelatedChangeSetsSection from "./WorkflowActivityRelatedChangeSetsSection";
import WorkflowActivityTypeSpecificContent from "./WorkflowActivityTypeSpecificContent";
import WorkflowActivityValidationResults from "./WorkflowActivityValidationResults";
import { getMainChangeSet, getSignalDisabledReason } from "./workflowDetailsHelpers";
import { WorkflowFieldCitationContextProvider } from "./WorkflowFieldCitationContext";
import WorkflowRunDetailsContext from "./WorkflowRunDetailsContext";
import WorkflowSupervisorConversationSection from "./WorkflowSupervisorConversationSection";

const reevaluateWorkflow = withErrorHandling(adminApi.reevaluateWorkflow);
const updateWorkflowActivityChangeSets = withErrorHandling(adminApi.updateWorkflowActivityChangeSets);

interface Props {
  activity: WorkflowActivity;
  onActionExecuted: (signalType?: WorkflowSignalType) => void;
  onWorkflowUpdated: (updatedWorkflow: Workflow) => void;
  onActivitySaved: (updatedActivity: WorkflowActivity) => void;
}

interface ActionDialogState {
  signalType: WorkflowSignalType;
  selectedChangeSetId?: string;
  selectedChangeSetName?: string;
  objectTypeName: string;
  objectName?: string;
}

const WorkflowActivityDetailContent = ({ activity, onActionExecuted, onWorkflowUpdated, onActivitySaved }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { workflowDetails, viewCitations } = useDefinedContext(WorkflowRunDetailsContext, "WorkflowRunDetailsContext");

  const workflowId = workflowDetails.workflow.id;
  const canCurrentUserTriggerActions = workflowDetails.canCurrentUserTriggerActions;

  const [actionDialogState, setActionDialogState] = useState<ActionDialogState>();
  const [showEditActivityDialog, setShowEditActivityDialog] = useState(false);
  const [selectedRelatedChangeSet, setSelectedRelatedChangeSet] = useState<WorkflowActivityChangeSet>();
  const [reevaluating, setReevaluating] = useState(false);
  const [inlineEditState, setInlineEditState] = useState(() =>
    getInitialInlineChangeSetEditState(activity, canCurrentUserTriggerActions)
  );
  const [savingInlineEdit, setSavingInlineEdit] = useState(false);

  const mainChangeSet = useMemo(() => getMainChangeSet(activity), [activity]);

  const handleUseMainChangeSet = useCallback(() => {
    setSelectedRelatedChangeSet(undefined);
  }, []);

  const handleUseRelatedChangeSet = useCallback((changeSet: WorkflowActivityChangeSet) => {
    setSelectedRelatedChangeSet(changeSet);
  }, []);

  const handleReevaluateClick = useCallback(async () => {
    setReevaluating(true);
    const [updatedWorkflow, error] = await reevaluateWorkflow(workflowId);
    setReevaluating(false);

    if (error) {
      logError(error, "[WorkflowActivityDetailContent] reevaluateWorkflow");
      sendNotificationError(error.message);
      return;
    }

    const updatedActivity = updatedWorkflow.activities.find(
      (a) => a.workflowActivityId === activity.workflowActivityId
    );
    setInlineEditState(
      updatedActivity ? getInitialInlineChangeSetEditState(updatedActivity, canCurrentUserTriggerActions) : undefined
    );
    onWorkflowUpdated(updatedWorkflow);
  }, [activity.workflowActivityId, canCurrentUserTriggerActions, onWorkflowUpdated, sendNotificationError, workflowId]);

  const handleInlineEditFieldChange = useCallback((fieldName: string, value: unknown) => {
    setInlineEditState((state) => (state ? updateChangeSetFieldAction(fieldName, value)(state) : state));
  }, []);

  const handleInlineEditObjectChange = useCallback(
    (configuration: ObjectSelectConfiguration, selectedObject: ObjectFieldValues | null) => {
      setInlineEditState((state) =>
        state ? updateChangeSetFieldsAction(buildObjectFieldUpdates(configuration, selectedObject))(state) : state
      );
    },
    []
  );

  const handleSaveInlineEdit = async () => {
    if (!inlineEditState || !mainChangeSet || !activity.workflowActivityId) {
      return;
    }

    setSavingInlineEdit(true);
    const payload = mapStateToUpdateWorkflowActivityChangeSetsRequest(inlineEditState, mainChangeSet);
    const [updatedActivity, error] = await updateWorkflowActivityChangeSets(
      workflowId,
      activity.workflowActivityId,
      payload
    );
    setSavingInlineEdit(false);

    if (error) {
      logError(error, "[WorkflowActivityDetailContent] updateWorkflowActivityChangeSets");
      sendNotificationError(error.message);
      return;
    }

    setInlineEditState(getInitialInlineChangeSetEditState(updatedActivity, canCurrentUserTriggerActions));
    onActivitySaved(updatedActivity);
  };

  const handleCancelInlineEdit = () => {
    setInlineEditState(getInitialInlineChangeSetEditState(activity, canCurrentUserTriggerActions));
  };

  const handleExecuteAction = useCallback(
    (signalType: WorkflowSignalType) => {
      const changeSetToUse = selectedRelatedChangeSet ?? mainChangeSet;

      setActionDialogState({
        signalType,
        objectTypeName: changeSetToUse?.name || "activity",
        objectName: stringifyValue(changeSetToUse?.fields?.find((f) => f.name === "Name")?.newValue),
        selectedChangeSetId: selectedRelatedChangeSet?.id,
      });
    },
    [mainChangeSet, selectedRelatedChangeSet]
  );

  const handleViewCitations = (documentName: string) => {
    if (!activity.workflowActivityId) {
      return;
    }

    viewCitations(activity.workflowActivityId, documentName);
  };

  const handleActivitySaved = (updatedActivity: WorkflowActivity) => {
    onActivitySaved(updatedActivity);
    setShowEditActivityDialog(false);
  };

  const canSelectRelatedChangeSets = Boolean(
    mainChangeSet?.relatedChangeSets?.some((cs) => cs.changeSets.some((c) => c.action === "UseExisting"))
  );

  const activeInlineEditState = activity.state === "WaitingForSignal" ? inlineEditState : undefined;

  const selectedActionChangeSet = selectedRelatedChangeSet ?? mainChangeSet;
  const requiresObjectSelection = Boolean(activity.userEditConfiguration?.requiresObjectSelection);
  const hasApprovableSelectedChangeSet =
    selectedActionChangeSet?.action === "Create" || Boolean(selectedActionChangeSet?.objectId);
  const missingObjectSelectionReason =
    requiresObjectSelection && !hasApprovableSelectedChangeSet
      ? "Select an object before approving this step."
      : undefined;
  const missingRequiredFieldCaptions = getMissingRequiredFieldCaptions(mainChangeSet?.fields ?? []);
  const missingRequiredFieldsReason =
    missingRequiredFieldCaptions.length > 0
      ? `Fill in the required fields before approving: ${missingRequiredFieldCaptions.join(", ")}.`
      : undefined;
  const unsavedInlineEditsReason = activeInlineEditState?.isDirty
    ? "Save or cancel your changes before proceeding."
    : undefined;

  const conclusionLogs = activity.executionLogs?.filter((log) => log.kind === "Conclusion") ?? [];

  return (
    <>
      <ScrollableFlexContainer>
        <Stack spacing={4} sx={{ px: 3, py: 2.5 }}>
          {conclusionLogs.length > 0 && (
            <Alert severity="info">
              <MarkdownPreview
                content={conclusionLogs
                  .map((log) => log.message)
                  .filter(Boolean)
                  .join("\n\n")}
              />
            </Alert>
          )}

          <WorkflowActivityDetailInfoSection activity={activity} />

          {mainChangeSet && mainChangeSet.fields.length > 0 && activity.workflowActivityId && (
            <WorkflowFieldCitationContextProvider
              activityId={activity.workflowActivityId}
              citations={mainChangeSet.citations}
            >
              {activeInlineEditState !== undefined ? (
                <ChangeSetInlineEditSection
                  changeSet={mainChangeSet}
                  editState={activeInlineEditState}
                  editConfiguration={activity.userEditConfiguration}
                  onFieldChange={handleInlineEditFieldChange}
                  onObjectChange={handleInlineEditObjectChange}
                />
              ) : (
                <WorkflowActivityChangeSetSection
                  changeSet={mainChangeSet}
                  showSourceColumn={activity.type === "ExtractFieldsFromDocument"}
                  isSelected={canSelectRelatedChangeSets && selectedRelatedChangeSet === undefined}
                  onSelectChangeSet={
                    canCurrentUserTriggerActions &&
                    canSelectRelatedChangeSets &&
                    activity.availableUserSignals.includes("Approve") &&
                    mainChangeSet.action !== "None"
                      ? handleUseMainChangeSet
                      : undefined
                  }
                />
              )}
            </WorkflowFieldCitationContextProvider>
          )}

          {mainChangeSet?.relatedChangeSets &&
            mainChangeSet.relatedChangeSets.map((relatedChangeSet) => (
              <WorkflowActivityRelatedChangeSetsSection
                key={relatedChangeSet.name}
                relatedChangeSet={relatedChangeSet}
                selectedRelatedChangeSetId={selectedRelatedChangeSet?.id}
                onSelectChangeSet={
                  canCurrentUserTriggerActions &&
                  activity.availableUserSignals.includes("Approve") &&
                  relatedChangeSet.changeSets.some((c) => c.action === "UseExisting")
                    ? handleUseRelatedChangeSet
                    : undefined
                }
              />
            ))}

          {mainChangeSet?.citations && mainChangeSet.citations.length > 0 && (
            <WorkflowActivityCitations citations={mainChangeSet.citations} onViewCitations={handleViewCitations} />
          )}

          {mainChangeSet?.validationResult && (
            <WorkflowActivityValidationResults validationResult={mainChangeSet.validationResult} />
          )}

          <WorkflowActivityTypeSpecificContent workflowId={workflowId} workflowActivity={activity} />

          <WorkflowSupervisorConversationSection
            workflowId={workflowId}
            activity={activity}
            isUserAllowedToReply={canCurrentUserTriggerActions}
            onResponseSubmitted={onActionExecuted}
          />

          <WorkflowActivityExecutionLogsSection activity={activity} />
        </Stack>
      </ScrollableFlexContainer>
      {canCurrentUserTriggerActions && activity.state === "WaitingForSignal" && (
        <GeneralPageFooter containerProps={{ sx: { justifyContent: "flex-start", columnGap: 2 } }}>
          {activeInlineEditState !== undefined && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveInlineEdit}
                loading={savingInlineEdit}
                disabled={!activeInlineEditState.isDirty || !activeInlineEditState.isValid}
              >
                Save
              </Button>

              <Button
                variant="text"
                color="secondary"
                onClick={handleCancelInlineEdit}
                disabled={!activeInlineEditState.isDirty || savingInlineEdit}
              >
                Cancel
              </Button>

              <Divider orientation="vertical" flexItem />
            </>
          )}

          {activity.availableUserSignals.map((signal) => {
            const disabledReason = getSignalDisabledReason(signal, {
              unsavedInlineEdits: unsavedInlineEditsReason,
              missingObjectSelection: missingObjectSelectionReason,
              missingRequiredFields: missingRequiredFieldsReason,
            });
            return (
              <WorkflowSignalActionButton
                key={signal}
                signalType={signal}
                loading={actionDialogState?.signalType === signal}
                disabled={actionDialogState !== undefined || disabledReason !== undefined}
                disabledReason={actionDialogState === undefined ? disabledReason : undefined}
                onClick={() => handleExecuteAction(signal)}
              />
            );
          })}

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleReevaluateClick}
            loading={reevaluating}
          >
            Reevaluate
          </Button>

          {activity.workflowActivityId &&
            activity.userEditConfiguration !== undefined &&
            activeInlineEditState === undefined && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<EditIcon />}
                onClick={() => setShowEditActivityDialog(true)}
              >
                Edit
              </Button>
            )}
        </GeneralPageFooter>
      )}
      {actionDialogState && (
        <WorkflowActionConfirmationDialog
          workflowId={workflowId}
          signalType={actionDialogState.signalType}
          selectedChangeSetId={actionDialogState.selectedChangeSetId}
          expectedActivityId={activity.workflowActivityId}
          objectTypeName={actionDialogState.objectTypeName}
          objectName={actionDialogState.objectName}
          onClose={() => setActionDialogState(undefined)}
          onActionExecuted={onActionExecuted}
        />
      )}
      {showEditActivityDialog && (
        <ActivityEditDialog
          workflowId={workflowId}
          activity={activity}
          isMainChangeSetSelected={selectedRelatedChangeSet === undefined}
          onClose={() => setShowEditActivityDialog(false)}
          onSaved={handleActivitySaved}
        />
      )}
    </>
  );
};

export default WorkflowActivityDetailContent;
